import * as React from 'react';
import { Check, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Popover } from '@/components/ui/popover';

/** On-brand dropdown with the API of a native `<select>`.
 *
 *  Children are the usual `<option>` / `<optgroup>` elements (fragments and
 *  arrays are fine). The trigger is a button that looks like the old styled
 *  native control; the list opens in our `Popover` instead of the OS menu
 *  (Alexey 04.11: «системных дропдаунов полно»).
 *
 *  A visually hidden native `<select>` with the same children stays in the
 *  DOM: it receives `ref`, `name`, `required`, `form`, `value`/`defaultValue`
 *  and `onChange`. Picking an item writes the value into it and dispatches a
 *  real bubbling `change` event, so `onChange={e => e.target.value}` (and
 *  `e.target.name`, form submission, parent `<form onChange>`) work as before.
 *  `multiple` / `size>1` fall back to the plain native control. */
export interface SelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  /** Classes for the wrapping `relative inline-block w-full` div. Use it to size
   *  the control in a toolbar, e.g. `w-auto` (shrink to content) or `w-40`. */
  wrapperClassName?: string;
}

interface Item {
  kind: 'option';
  value: string;
  label: string;
  disabled: boolean;
  hidden: boolean;
}
interface Group {
  kind: 'group';
  label: string;
}
type Row = Item | Group;

function textOf(node: React.ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(textOf).join('');
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) return textOf(node.props.children);
  return '';
}

function parseChildren(children: React.ReactNode, rows: Row[], groupDisabled = false) {
  React.Children.forEach(children, child => {
    if (!React.isValidElement(child)) return;
    const props = child.props as Record<string, unknown> & { children?: React.ReactNode };
    if (child.type === React.Fragment) {
      parseChildren(props.children, rows, groupDisabled);
    } else if (child.type === 'optgroup') {
      rows.push({ kind: 'group', label: String(props.label ?? '') });
      parseChildren(props.children, rows, groupDisabled || !!props.disabled);
    } else if (child.type === 'option') {
      const label = props.label != null ? String(props.label) : textOf(props.children);
      rows.push({
        kind: 'option',
        value: props.value != null ? String(props.value) : label,
        label,
        disabled: groupDisabled || !!props.disabled,
        hidden: !!props.hidden,
      });
    }
  });
}

const triggerBase =
  'h-9 w-full rounded-lg border border-input bg-background px-3 pr-8 py-1 text-sm shadow-sm transition-colors';
const triggerFocus =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:border-ring';
const triggerDisabled = 'disabled:cursor-not-allowed disabled:opacity-50';

const NativeSelect = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, wrapperClassName, children, ...props }, ref) => (
    <div className={cn('relative inline-block w-full', wrapperClassName)}>
      <select ref={ref} className={cn(triggerBase, 'appearance-none', triggerFocus, triggerDisabled, className)} {...props}>
        {children}
      </select>
      <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
    </div>
  ),
);
NativeSelect.displayName = 'NativeSelect';

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>((allProps, ref) => {
  if (allProps.multiple || (allProps.size != null && allProps.size > 1)) return <NativeSelect ref={ref} {...allProps} />;
  return <CustomSelect ref={ref} {...allProps} />;
});
Select.displayName = 'Select';

const CustomSelect = React.forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className, wrapperClassName, children,
      // → hidden native <select>
      value, defaultValue, onChange, onInput, name, required, form, autoComplete,
      // → both
      disabled,
      // → trigger button
      id, ...buttonProps
    },
    ref,
  ) => {
    const rows = React.useMemo(() => {
      const out: Row[] = [];
      parseChildren(children, out);
      return out;
    }, [children]);
    const items = React.useMemo(() => rows.filter((r): r is Item => r.kind === 'option'), [rows]);

    const controlled = value !== undefined;
    const [inner, setInner] = React.useState<string | undefined>(
      defaultValue !== undefined && !Array.isArray(defaultValue) ? String(defaultValue) : undefined,
    );
    const rawValue = controlled ? (Array.isArray(value) ? String(value[0] ?? '') : String(value)) : inner;
    // Same fallback as the native control: no match → first enabled option.
    const selected =
      (rawValue !== undefined ? items.find(i => i.value === rawValue) : undefined) ??
      items.find(i => !i.disabled) ??
      items[0];

    const selectRef = React.useRef<HTMLSelectElement | null>(null);
    const setSelectRef = React.useCallback(
      (el: HTMLSelectElement | null) => {
        selectRef.current = el;
        if (typeof ref === 'function') ref(el);
        else if (ref) (ref as React.MutableRefObject<HTMLSelectElement | null>).current = el;
      },
      [ref],
    );
    const triggerRef = React.useRef<HTMLButtonElement>(null);
    const listRef = React.useRef<HTMLDivElement>(null);
    const [open, setOpenState] = React.useState(false);
    const [active, setActive] = React.useState(-1);
    const [triggerWidth, setTriggerWidth] = React.useState(0);
    const baseId = React.useId();
    const listId = `${baseId}-list`;
    const optId = (i: number) => `${baseId}-opt-${i}`;

    const enabled = (i: number) => i >= 0 && i < items.length && !items[i].disabled && !items[i].hidden;

    const setOpen = React.useCallback(
      (next: boolean) => {
        if (next) {
          if (disabled) return;
          if (triggerRef.current) setTriggerWidth(triggerRef.current.getBoundingClientRect().width);
          const idx = selected ? items.indexOf(selected) : -1;
          setActive(idx >= 0 && !items[idx].hidden ? idx : items.findIndex(i => !i.disabled && !i.hidden));
        }
        setOpenState(next);
      },
      [disabled, items, selected],
    );
    const onOpenChange = React.useCallback((next: boolean) => setOpen(next), [setOpen]);

    React.useEffect(() => {
      if (disabled && open) setOpenState(false);
    }, [disabled, open]);

    const commit = (item: Item) => {
      setOpenState(false);
      triggerRef.current?.focus();
      if (item.disabled || item.value === selected?.value) return;
      if (!controlled) setInner(item.value);
      const el = selectRef.current;
      if (el) {
        el.value = item.value;
        // React's onChange for <select> listens to the native `change` event.
        el.dispatchEvent(new Event('input', { bubbles: true }));
        el.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };

    const move = (from: number, step: 1 | -1) => {
      for (let i = from + step; i >= 0 && i < items.length; i += step) if (enabled(i)) return i;
      return from;
    };

    // Type-to-jump.
    const typed = React.useRef({ buf: '', at: 0 });
    const typeahead = (ch: string) => {
      const now = Date.now();
      const t = typed.current;
      t.buf = now - t.at > 700 ? ch : t.buf + ch;
      t.at = now;
      const q = t.buf.toLocaleLowerCase();
      const start = open ? active : selected ? items.indexOf(selected) : -1;
      const order = [...items.keys()].map(k => (k + start + (t.buf.length === 1 ? 1 : 0) + items.length) % items.length);
      const hit = order.find(i => enabled(i) && items[i].label.trim().toLocaleLowerCase().startsWith(q));
      if (hit === undefined) return;
      if (!open) setOpen(true);
      setActive(hit);
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
      (buttonProps.onKeyDown as unknown as ((e: React.KeyboardEvent<HTMLButtonElement>) => void) | undefined)?.(e);
      if (e.defaultPrevented || disabled) return;
      const k = e.key;
      if (!open) {
        if (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ') {
          e.preventDefault();
          setOpen(true);
        } else if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
          e.preventDefault();
          typeahead(k);
        }
        return;
      }
      if (k === 'Escape') {
        e.preventDefault();
        // Don't let the surrounding Dialog/Sheet close on the same keypress.
        e.nativeEvent.stopImmediatePropagation();
        e.stopPropagation();
        setOpenState(false);
      } else if (k === 'Tab') {
        setOpenState(false);
      } else if (k === 'ArrowDown') {
        e.preventDefault();
        setActive(a => move(a, 1));
      } else if (k === 'ArrowUp') {
        e.preventDefault();
        setActive(a => move(a, -1));
      } else if (k === 'Home') {
        e.preventDefault();
        setActive(move(-1, 1));
      } else if (k === 'End') {
        e.preventDefault();
        setActive(move(items.length, -1));
      } else if (k === 'PageDown' || k === 'PageUp') {
        e.preventDefault();
        const step = k === 'PageDown' ? 1 : -1;
        setActive(a => {
          let cur = a;
          for (let n = 0; n < 8; n++) cur = move(cur, step);
          return cur;
        });
      } else if (k === 'Enter' || (k === ' ' && Date.now() - typed.current.at > 700)) {
        e.preventDefault();
        if (enabled(active)) commit(items[active]);
        else setOpenState(false);
      } else if (k.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
        e.preventDefault();
        typeahead(k);
      }
    };

    // Keep the active item in view (centre it on open). Manual scrollTop, not
    // scrollIntoView: the popover is parked off-screen for its first frame.
    const justOpened = React.useRef(false);
    React.useEffect(() => {
      if (open) justOpened.current = true;
    }, [open]);
    React.useLayoutEffect(() => {
      if (!open) return;
      const list = listRef.current;
      const el = active >= 0 ? (list?.querySelector(`[data-idx="${active}"]`) as HTMLElement | null) : null;
      if (!list || !el) return;
      if (justOpened.current) {
        justOpened.current = false;
        list.scrollTop = el.offsetTop - (list.clientHeight - el.offsetHeight) / 2;
        return;
      }
      if (el.offsetTop < list.scrollTop) list.scrollTop = el.offsetTop - 4;
      else if (el.offsetTop + el.offsetHeight > list.scrollTop + list.clientHeight)
        list.scrollTop = el.offsetTop + el.offsetHeight - list.clientHeight + 4;
    }, [open, active]);

    let itemIdx = -1;
    return (
      <div className={cn('relative inline-block w-full', wrapperClassName)}>
        <select
          ref={setSelectRef}
          value={value}
          defaultValue={defaultValue}
          onChange={onChange}
          onInput={onInput}
          name={name}
          required={required}
          form={form}
          autoComplete={autoComplete}
          disabled={disabled}
          tabIndex={-1}
          aria-hidden
          // Form validation focuses the invalid control — hand focus to the button.
          onFocus={() => triggerRef.current?.focus()}
          className="pointer-events-none absolute inset-0 h-full w-full opacity-0"
        >
          {children}
        </select>
        <button
          {...(buttonProps as unknown as React.ButtonHTMLAttributes<HTMLButtonElement>)}
          ref={triggerRef}
          id={id}
          type="button"
          disabled={disabled}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={open ? listId : undefined}
          aria-activedescendant={open && active >= 0 ? optId(active) : undefined}
          onClick={e => {
            (buttonProps.onClick as unknown as ((e: React.MouseEvent<HTMLButtonElement>) => void) | undefined)?.(e);
            if (!e.defaultPrevented) setOpen(!open);
          }}
          onKeyDown={onKeyDown}
          className={cn(
            triggerBase,
            'relative flex items-center text-left',
            triggerFocus,
            triggerDisabled,
            open && 'border-ring ring-2 ring-ring/30',
            className,
          )}
        >
          {/* All labels stacked in one grid cell → width = widest option, like
              the native control in `w-auto` toolbars; only the selected one is visible. */}
          <span className="grid min-w-0 flex-1 grid-cols-[minmax(0,1fr)]">
            {items.length === 0 && <span className="col-start-1 row-start-1">{' '}</span>}
            {items.map((it, i) => (
              <span
                key={i}
                aria-hidden={it !== selected}
                className={cn('col-start-1 row-start-1 truncate', it !== selected && 'invisible')}
              >
                {it.label || ' '}
              </span>
            ))}
          </span>
        </button>
        <ChevronDown
          className={cn(
            'pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 size-4 text-muted-foreground transition-transform duration-150',
            open && 'rotate-180',
            disabled && 'opacity-50',
          )}
        />
        <Popover
          open={open}
          onOpenChange={onOpenChange}
          triggerRef={triggerRef}
          side="bottom"
          align="start"
          sideOffset={4}
          // Above our Dialog/Sheet (z-50) and MUI modals (z-1300).
          className="z-[1500] max-w-[calc(100vw-1rem)]"
        >
          <div
            ref={listRef}
            id={listId}
            role="listbox"
            data-overlay
            className="relative flex max-h-72 flex-col overflow-y-auto overscroll-contain thin-scrollbar p-1"
            style={{ minWidth: triggerWidth }}
            // Keep focus on the trigger (keyboard handling lives there) and keep
            // clicks from bubbling to React ancestors (clickable rows, etc.).
            onMouseDown={e => { e.preventDefault(); e.stopPropagation(); }}
            onClick={e => e.stopPropagation()}
          >
            {rows.map((row, r) => {
              if (row.kind === 'group') {
                return (
                  <div
                    key={`g${r}`}
                    role="presentation"
                    className="px-2 pb-1 pt-2 text-xs font-medium text-muted-foreground first:pt-1"
                  >
                    {row.label}
                  </div>
                );
              }
              itemIdx += 1;
              const i = itemIdx;
              if (row.hidden) return null;
              const isSel = row === selected;
              return (
                <div
                  key={`o${r}`}
                  id={optId(i)}
                  data-idx={i}
                  role="option"
                  aria-selected={isSel}
                  aria-disabled={row.disabled || undefined}
                  onMouseMove={() => { if (!row.disabled && active !== i) setActive(i); }}
                  onClick={() => { if (!row.disabled) commit(row); }}
                  className={cn(
                    'flex cursor-pointer select-none items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm transition-colors',
                    isSel && 'bg-primary-muted text-primary-muted-foreground',
                    !isSel && i === active && 'bg-muted text-foreground',
                    isSel && i === active && 'ring-1 ring-inset ring-primary/30',
                    row.disabled && 'cursor-not-allowed opacity-50',
                  )}
                >
                  <span className="min-w-0 flex-1 break-words">{row.label || ' '}</span>
                  <Check className={cn('size-4 shrink-0', !isSel && 'invisible')} />
                </div>
              );
            })}
          </div>
        </Popover>
      </div>
    );
  },
);
CustomSelect.displayName = 'CustomSelect';
